import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { getBrowserInfo, BrowserInfo, BrowserType } from '@/lib/browser-detection';

/**
 * Configurações de otimização baseadas no browser
 */
export interface PerformanceOptimizations {
  // Otimizações de imagem
  useWebP: boolean;
  useAVIF: boolean;
  useResponsiveImages: boolean;
  imageQuality: 'high' | 'medium' | 'low';
  
  // Otimizações de cache
  aggressiveCache: boolean;
  cacheStrategy: 'long' | 'medium' | 'short' | 'none';
  
  // Otimizações de analytics
  delayedAnalytics: boolean;
  analyticsRetry: boolean;
  backupTracking: boolean;
  
  // Otimizações de interface
  simplifiedAnimations: boolean;
  reducedMotion: boolean;
  optimizedForms: boolean;
  
  // Otimizações de loading
  eagerLoading: boolean;
  lazyLoading: boolean;
  preloadCritical: boolean;
  
  // Otimizações específicas
  showEscapeButton: boolean;
  optimizedTouch: boolean;
  preventZoom: boolean;
}

/**
 * Contexto de performance
 */
export interface PerformanceContextType {
  // Informações do browser
  browserInfo: BrowserInfo;
  browserType: BrowserType;
  isWebView: boolean;
  isMobile: boolean;
  
  // Otimizações ativas
  optimizations: PerformanceOptimizations;
  
  // Métodos
  updateOptimizations: (updates: Partial<PerformanceOptimizations>) => void;
  getOptimization: (key: keyof PerformanceOptimizations) => boolean | string;
  
  // Debug
  debugMode: boolean;
  toggleDebug: () => void;
}

/**
 * Contexto padrão
 */
const defaultContext: PerformanceContextType = {
  browserInfo: {
    type: 'standard',
    isWebView: false,
    isInstagram: false,
    isFacebook: false,
    isTikTok: false,
    isWhatsApp: false,
    isMobile: false,
    userAgent: ''
  },
  browserType: 'standard',
  isWebView: false,
  isMobile: false,
  optimizations: {
    useWebP: true,
    useAVIF: true,
    useResponsiveImages: true,
    imageQuality: 'high',
    aggressiveCache: true,
    cacheStrategy: 'long',
    delayedAnalytics: true,
    analyticsRetry: false,
    backupTracking: false,
    simplifiedAnimations: false,
    reducedMotion: false,
    optimizedForms: false,
    eagerLoading: false,
    lazyLoading: true,
    preloadCritical: true,
    showEscapeButton: false,
    optimizedTouch: false,
    preventZoom: false
  },
  updateOptimizations: () => {},
  getOptimization: () => false,
  debugMode: false,
  toggleDebug: () => {}
};

/**
 * Contexto de Performance
 */
const PerformanceContext = createContext<PerformanceContextType>(defaultContext);

/**
 * Gera otimizações baseadas no tipo de browser
 */
const generateOptimizations = (browserInfo: BrowserInfo): PerformanceOptimizations => {
  const { type, isWebView, isMobile } = browserInfo;
  
  // Instagram optimizations
  if (type === 'instagram') {
    return {
      // Imagens: JPG para compatibilidade
      useWebP: false,
      useAVIF: false,
      useResponsiveImages: true,
      imageQuality: 'medium',
      
      // Cache: Menos agressivo
      aggressiveCache: false,
      cacheStrategy: 'short',
      
      // Analytics: Imediato com retry
      delayedAnalytics: false,
      analyticsRetry: true,
      backupTracking: true,
      
      // Interface: Simplificada
      simplifiedAnimations: true,
      reducedMotion: true,
      optimizedForms: true,
      
      // Loading: Mais eager
      eagerLoading: true,
      lazyLoading: false,
      preloadCritical: true,
      
      // Específicas: UI melhorada
      showEscapeButton: true,
      optimizedTouch: true,
      preventZoom: true
    };
  }
  
  // Facebook optimizations
  if (type === 'facebook') {
    return {
      useWebP: true,
      useAVIF: false,
      useResponsiveImages: true,
      imageQuality: 'medium',
      aggressiveCache: false,
      cacheStrategy: 'medium',
      delayedAnalytics: false,
      analyticsRetry: true,
      backupTracking: true,
      simplifiedAnimations: true,
      reducedMotion: false,
      optimizedForms: true,
      eagerLoading: true,
      lazyLoading: true,
      preloadCritical: true,
      showEscapeButton: false,
      optimizedTouch: true,
      preventZoom: false
    };
  }
  
  // Outros WebViews (TikTok, WhatsApp, etc.)
  if (isWebView) {
    return {
      useWebP: true,
      useAVIF: false,
      useResponsiveImages: true,
      imageQuality: 'medium',
      aggressiveCache: false,
      cacheStrategy: 'medium',
      delayedAnalytics: false,
      analyticsRetry: false,
      backupTracking: false,
      simplifiedAnimations: true,
      reducedMotion: false,
      optimizedForms: true,
      eagerLoading: false,
      lazyLoading: true,
      preloadCritical: true,
      showEscapeButton: false,
      optimizedTouch: isMobile,
      preventZoom: false
    };
  }
  
  // Navegadores padrão: performance máxima
  return {
    useWebP: true,
    useAVIF: true,
    useResponsiveImages: true,
    imageQuality: 'high',
    aggressiveCache: true,
    cacheStrategy: 'long',
    delayedAnalytics: true,
    analyticsRetry: false,
    backupTracking: false,
    simplifiedAnimations: false,
    reducedMotion: false,
    optimizedForms: false,
    eagerLoading: false,
    lazyLoading: true,
    preloadCritical: true,
    showEscapeButton: false,
    optimizedTouch: isMobile,
    preventZoom: false
  };
};

/**
 * Props do Provider
 */
interface PerformanceProviderProps {
  children: ReactNode;
  debugMode?: boolean;
  customOptimizations?: Partial<PerformanceOptimizations>;
}

/**
 * Provider de Performance
 */
export const PerformanceProvider: React.FC<PerformanceProviderProps> = ({
  children,
  debugMode: initialDebugMode = false,
  customOptimizations = {}
}) => {
  const [browserInfo, setBrowserInfo] = useState<BrowserInfo>(defaultContext.browserInfo);
  const [optimizations, setOptimizations] = useState<PerformanceOptimizations>(defaultContext.optimizations);
  const [debugMode, setDebugMode] = useState(initialDebugMode);
  
  // Memoizar customOptimizations para evitar re-renders desnecessários
  const memoizedCustomOptimizations = useMemo(() => customOptimizations, [JSON.stringify(customOptimizations)]);
  
  // Inicializar detecção de browser (executa apenas uma vez)
  useEffect(() => {
    const info = getBrowserInfo();
    setBrowserInfo(info);
    
    // Gerar otimizações baseadas no browser
    const generatedOptimizations = generateOptimizations(info);
    const finalOptimizations = { ...generatedOptimizations, ...memoizedCustomOptimizations };
    setOptimizations(finalOptimizations);
    
    // Debug log
    if (debugMode || initialDebugMode) {
      console.group('🚀 Performance Provider Initialized');
      console.log('Browser Info:', info);
      console.log('Optimizations:', finalOptimizations);
      console.groupEnd();
    }
  }, [memoizedCustomOptimizations, initialDebugMode]); // Removido debugMode da dependência
  
  // Efeito separado para debug mode changes
  useEffect(() => {
    if (debugMode !== initialDebugMode) {
      console.log('🔧 Debug Mode Changed:', debugMode);
    }
  }, [debugMode, initialDebugMode]);
  
  // Atualizar otimizações (com proteção contra loops e memoização)
  const updateOptimizations = useMemo(() => 
    (updates: Partial<PerformanceOptimizations>) => {
      setOptimizations(prev => {
        const newOptimizations = { ...prev, ...updates };
        
        // Verificar se realmente mudou para evitar re-renders desnecessários
        const hasChanges = Object.keys(updates).some(key => 
          prev[key as keyof PerformanceOptimizations] !== updates[key as keyof PerformanceOptimizations]
        );
        
        if (!hasChanges) {
          return prev; // Retorna o mesmo objeto se não houve mudanças
        }
        
        if (debugMode) {
          console.log('🔧 Optimizations Updated:', updates);
        }
        
        return newOptimizations;
      });
    },
    [debugMode]
  );
  
  // Obter otimização específica (memoizada)
  const getOptimization = useMemo(() => 
    (key: keyof PerformanceOptimizations) => optimizations[key],
    [optimizations]
  );
  
  // Toggle debug mode (memoizado)
  const toggleDebug = useMemo(() => 
    () => setDebugMode(prev => !prev),
    []
  );
  
  // Memoizar o contextValue para evitar re-renders desnecessários
  const contextValue: PerformanceContextType = useMemo(() => ({
    browserInfo,
    browserType: browserInfo.type,
    isWebView: browserInfo.isWebView,
    isMobile: browserInfo.isMobile,
    optimizations,
    updateOptimizations,
    getOptimization,
    debugMode,
    toggleDebug
  }), [browserInfo, optimizations, debugMode]);
  
  return (
    <PerformanceContext.Provider value={contextValue}>
      {children}
      {debugMode && <PerformanceDebugPanel />}
    </PerformanceContext.Provider>
  );
};

/**
 * Hook para usar o contexto de performance
 */
export const usePerformanceContext = (): PerformanceContextType => {
  const context = useContext(PerformanceContext);
  
  if (!context) {
    throw new Error('usePerformanceContext must be used within a PerformanceProvider');
  }
  
  return context;
};

/**
 * Hook simplificado para otimizações
 */
export const useOptimizations = () => {
  const { optimizations, browserInfo } = usePerformanceContext();
  return { optimizations, browserInfo };
};

/**
 * Hook para detecção de browser
 */
export const useBrowserInfo = () => {
  const { browserInfo } = usePerformanceContext();
  return browserInfo;
};

/**
 * Componente de Debug (opcional)
 */
const PerformanceDebugPanel: React.FC = () => {
  const context = usePerformanceContext();
  
  if (!context.debugMode) return null;
  
  return (
    <div 
      style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        background: 'rgba(0,0,0,0.9)',
        color: 'white',
        padding: '10px',
        borderRadius: '8px',
        fontSize: '12px',
        maxWidth: '300px',
        zIndex: 9999,
        fontFamily: 'monospace'
      }}
    >
      <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>
        🚀 Performance Debug
      </div>
      <div>Browser: {context.browserType}</div>
      <div>WebView: {context.isWebView ? 'Yes' : 'No'}</div>
      <div>Mobile: {context.isMobile ? 'Yes' : 'No'}</div>
      <div style={{ marginTop: '8px', fontSize: '11px' }}>
        WebP: {context.optimizations.useWebP ? '✅' : '❌'} | 
        AVIF: {context.optimizations.useAVIF ? '✅' : '❌'} | 
        Eager: {context.optimizations.eagerLoading ? '✅' : '❌'}
      </div>
      <button 
        onClick={context.toggleDebug}
        style={{
          marginTop: '8px',
          padding: '4px 8px',
          background: '#333',
          border: 'none',
          color: 'white',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Hide Debug
      </button>
    </div>
  );
};

export default PerformanceProvider;
